<template>
  <el-dialog
    :model-value="visible"
    title="文件夹权限配置"
    width="760px"
    style="max-width: 92vw"
    :close-on-click-modal="false"
    @update:model-value="$emit('update:visible', $event)"
    @open="fetchAcl"
  >
    <div v-loading="loading">
      <div class="mb-3 flex items-center gap-4">
        <span class="text-sm text-slate-500">文件夹：</span>
        <span class="text-sm font-medium text-slate-700">{{ folderName }}</span>
      </div>
      <AssetFolderAclEditor
        v-model:visibility-type="visibilityType"
        v-model:acl-items="aclItems"
      />
    </div>

    <template #footer>
      <el-button @click="$emit('update:visible', false)">取消</el-button>
      <el-button type="primary" :loading="saving" @click="handleSave">保存</el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref } from 'vue'
import { ElMessage } from 'element-plus'
import { updateFolderAcl } from '@/api/assets'
import AssetFolderAclEditor from './AssetFolderAclEditor.vue'

const props = defineProps({
  visible: { type: Boolean, default: false },
  folderId: { type: [Number, String], default: null },
  folderName: { type: String, default: '' },
  folder: { type: Object, default: null }
})

const emit = defineEmits(['update:visible', 'success'])

const loading = ref(false)
const saving = ref(false)
const visibilityType = ref('INHERIT')
const aclItems = ref([])

async function fetchAcl() {
  if (!props.folderId) return
  loading.value = true
  try {
    const data = props.folder || {}
    visibilityType.value = data.visibilityType || 'INHERIT'
    const aclList = data.aclItems || data.aclList || []
    aclItems.value = aclList
      .filter(item => item.subjectType !== 'ROLE')
      .map(item => ({
        subjectType: item.subjectType || 'USER',
        subjectValue: item.subjectType === 'ALL_AUTHENTICATED' ? [] : [item.subjectId],
        permissionLevel: item.permissionLevel || 'VIEW'
      }))
  } catch {
    // handled by interceptor
  } finally {
    loading.value = false
  }
}

function normalizeSubjectIds(item) {
  if (item.subjectType === 'ALL_AUTHENTICATED') return 0
  const value = item.subjectValue
  const values = Array.isArray(value) ? value : [value]
  return values
    .map(subject => {
      if (subject && typeof subject === 'object') return Number(subject.id) || 0
      return Number(subject) || 0
    })
    .filter(Boolean)
}

function expandAclItems() {
  return aclItems.value.flatMap(item => {
    if (item.subjectType === 'ALL_AUTHENTICATED') {
      return [{
        subjectType: item.subjectType,
        subjectId: normalizeSubjectIds(item),
        permissionLevel: item.permissionLevel
      }]
    }
    return normalizeSubjectIds(item).map(subjectId => ({
      subjectType: item.subjectType,
      subjectId,
      permissionLevel: item.permissionLevel
    }))
  })
}

async function handleSave() {
  saving.value = true
  try {
    const body = {
      visibilityType: visibilityType.value,
      aclList: visibilityType.value === 'CUSTOM'
        ? expandAclItems()
        : []
    }
    await updateFolderAcl(props.folderId, body)
    ElMessage.success('权限配置已保存')
    emit('success')
    emit('update:visible', false)
  } catch {
    // handled by interceptor
  } finally {
    saving.value = false
  }
}
</script>
